import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import Loader2Icon from "lucide-react/dist/esm/icons/loader-2";
import type { SuggestionKeyDownProps, SuggestionProps } from "@tiptap/suggestion";
import type { RecordMentionItem } from "./types";
import { getRecordIcon } from "../../get-record-icon";
import { humanDate } from "../../human-date-with-tooltip";

export interface RecordMentionSuggestionMenuRef {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
}

type RecordMentionSuggestionProps = SuggestionProps<RecordMentionItem> & {
  /** Set by the suggestion renderer while the async `items` query is in flight. */
  isLoading?: boolean;
};

function formatRecordSource(source: string) {
  return source
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatCreatedAt(createdAt: RecordMentionItem["createdAt"]) {
  if (!createdAt) return null;

  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return null;

  return humanDate(date);
}

export const RecordMentionSuggestionMenu = forwardRef<RecordMentionSuggestionMenuRef, RecordMentionSuggestionProps>(
  function RecordMentionSuggestionMenu({ command, items, isLoading = false }, ref) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

    const selectItem = (index: number) => {
      const item = items[index];
      if (!item) return;
      command(item);
    };

    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    useEffect(() => {
      const el = itemRefs.current[selectedIndex];
      if (el) {
        el.scrollIntoView({ block: "nearest" });
      }
    }, [selectedIndex]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (isLoading || !items.length) {
          if (event.key === "ArrowUp" || event.key === "ArrowDown" || event.key === "Enter") {
            event.preventDefault();
            return true;
          }
          return false;
        }

        if (event.key === "ArrowUp") {
          event.preventDefault();
          setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
          return true;
        }

        if (event.key === "ArrowDown") {
          event.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % items.length);
          return true;
        }

        if (event.key === "Enter") {
          event.preventDefault();
          selectItem(selectedIndex);
          return true;
        }

        return false;
      },
    }));

    const showEmpty = !isLoading && !items.length;

    return (
      <div
        data-record-mention-suggestion="true"
        className="z-50 min-w-[20rem] bg-popover text-popover-foreground pointer-events-auto"
      >
        {isLoading ? (
          <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 shrink-0 animate-spin" aria-hidden />
            <span>Searching users and records…</span>
          </div>
        ) : showEmpty ? (
          <div className="px-3 py-2.5 text-sm text-muted-foreground">No matching users or records.</div>
        ) : (
          <div className="max-h-72 overflow-auto p-1">
            {items.map((item, index) => {
              const isSelected = index === selectedIndex;
              const status = item.status?.trim();
              const createdAt = formatCreatedAt(item.createdAt);
              const metadata = [
                formatRecordSource(item.source),
                item.source === "user" ? item.tags?.filter(Boolean).join(" • ") : null,
                status,
                item.source !== "user" && createdAt ? `Created ${createdAt}` : null,
              ].filter(Boolean);

              return (
                <button
                  type="button"
                  key={`${item.source}-${item.id}`}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  className={[
                    "flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs transition-colors",
                    isSelected
                      ? "bg-accent text-accent-foreground font-medium"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                  ].join(" ")}
                  onMouseDown={(event) => {
                    // Keep editor selection/range stable so mention insert succeeds.
                    event.preventDefault();
                  }}
                  onClick={() => selectItem(index)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  {getRecordIcon(item.icon, item.source)}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{item.label || item.id}</div>
                    <div className="truncate text-xs text-muted-foreground">{metadata.join(" • ")}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);
